import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { PdfToolbar, PdfThumbRail } from "./PdfViewerParts";

const clamp = (z) => Math.min(4, Math.max(0.5, z));

/* 服务端逐页渲染的 PDF 查看器：左侧缩略图跳页 · 捏合 / Ctrl+滚轮 / 按钮缩放 · 全屏（原生 API，iPhone 回退为覆盖层）。 */
export const PdfViewer = ({ meta, pageUrl, downloadUrl, zh, extra, tp = "pdf", initialThumbnails }) => {
  const N = meta.pageCount;
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [thumbs, setThumbs] = useState(() => initialThumbnails ?? (typeof window === "undefined" || window.innerWidth >= 640));
  const [fs, setFs] = useState(false);
  const [pseudo, setPseudo] = useState(false);
  const [st, setSt] = useState({});
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const zoomRef = useRef(1);
  const skipRef = useRef(0);
  zoomRef.current = zoom;

  const goTo = useCallback((n) => {
    const el = scrollRef.current?.querySelector(`[data-page="${n}"]`);
    skipRef.current = Date.now() + 400;
    if (el && scrollRef.current) scrollRef.current.scrollTo({ top: el.offsetTop - 8, behavior: "auto" });
    setPage(n);
  }, []);

  const onScroll = () => {
    const c = scrollRef.current; if (!c || Date.now() < skipRef.current) return;
    const probe = c.getBoundingClientRect().top + c.clientHeight * 0.4;
    let best = 1, bestD = Infinity;
    c.querySelectorAll("[data-page]").forEach((el) => {
      const r = el.getBoundingClientRect();
      const d = r.top <= probe && r.bottom >= probe ? 0 : Math.min(Math.abs(r.top - probe), Math.abs(r.bottom - probe));
      if (d < bestD) { bestD = d; best = Number(el.dataset.page); }
    });
    setPage(best);
  };

  useEffect(() => {
    const c = scrollRef.current; if (!c) return undefined;
    let d0 = 0, z0 = 1;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const ts = (e) => { if (e.touches.length === 2) { d0 = dist(e.touches); z0 = zoomRef.current; } };
    const tm = (e) => { if (e.touches.length === 2 && d0) { e.preventDefault(); setZoom(clamp(z0 * dist(e.touches) / d0)); } };
    const te = () => { d0 = 0; };
    const wh = (e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); setZoom((z) => clamp(z * (e.deltaY < 0 ? 1.1 : 0.9))); } };
    c.addEventListener("touchstart", ts, { passive: true }); c.addEventListener("touchmove", tm, { passive: false });
    c.addEventListener("touchend", te); c.addEventListener("wheel", wh, { passive: false });
    return () => { c.removeEventListener("touchstart", ts); c.removeEventListener("touchmove", tm); c.removeEventListener("touchend", te); c.removeEventListener("wheel", wh); };
  }, [pseudo]);

  useEffect(() => {
    const h = () => setFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", h); document.addEventListener("webkitfullscreenchange", h);
    return () => { document.removeEventListener("fullscreenchange", h); document.removeEventListener("webkitfullscreenchange", h); };
  }, []);

  const toggleFs = () => {
    if (pseudo) { setPseudo(false); return; }
    if (document.fullscreenElement || document.webkitFullscreenElement) { (document.exitFullscreen || document.webkitExitFullscreen).call(document); return; }
    const el = rootRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => setPseudo(true));
    else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else setPseudo(true);
  };

  const body = (
    <div ref={rootRef} data-testid={`${tp}-viewer`} className={`flex flex-col bg-slate-900 text-slate-100 overflow-hidden select-none ${pseudo ? "fixed inset-0 z-[10000]" : "h-full rounded-xl"}`}>
      <PdfToolbar zh={zh} page={page} total={N} zoom={zoom} thumbs={thumbs} fs={fs || pseudo} extra={extra} downloadUrl={downloadUrl} tp={tp}
        onPrev={() => goTo(Math.max(1, page - 1))} onNext={() => goTo(Math.min(N, page + 1))} onThumbs={() => setThumbs((v) => !v)}
        onZoomIn={() => setZoom((z) => clamp(z * 1.25))} onZoomOut={() => setZoom((z) => clamp(z / 1.25))} onFit={() => setZoom(1)} onFs={toggleFs} />
      <div className="flex flex-1 min-h-0">
        {thumbs && <PdfThumbRail meta={meta} pageUrl={pageUrl} page={page} goTo={goTo} tp={tp} />}
        <div ref={scrollRef} onScroll={onScroll} data-testid={`${tp}-scroll`} className="relative flex-1 overflow-auto bg-slate-800/60 p-2 sm:p-4" style={{ touchAction: "pan-x pan-y" }}>
          {meta.sizes.map(([w, h], i) => (
            <div key={i} data-page={i + 1} className="relative mx-auto mb-3 bg-white shadow-lg shadow-black/40" style={{ width: `${zoom * 100}%`, aspectRatio: `${w} / ${h}` }}>
              <span className={`absolute inset-0 flex items-center justify-center gap-2 text-xs ${st[i + 1] === "err" ? "text-red-500" : "text-slate-400"}`} data-testid={`${tp}-page-state-${i + 1}`}>
                {st[i + 1] === "err" ? (zh ? `第 ${i + 1} 页渲染失败` : `Page ${i + 1} failed to render`) : <><Loader2 className="h-4 w-4 animate-spin" />{zh ? `第 ${i + 1} 页` : `Page ${i + 1}`}</>}
              </span>
              <img src={pageUrl(i + 1, "l")} alt={`page ${i + 1}`} loading="lazy" draggable={false} onLoad={() => setSt((m) => ({ ...m, [i + 1]: "ok" }))} onError={() => setSt((m) => ({ ...m, [i + 1]: "err" }))}
                className={`relative w-full h-full block transition-opacity duration-300 ${st[i + 1] === "ok" ? "opacity-100" : "opacity-0"}`} data-testid={`${tp}-page-${i + 1}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  return pseudo ? createPortal(body, document.body) : body;
};
